import type { WindowCoverage } from '../creatives/contract'
import type { ConfidenceThresholds } from '../creatives/confidence'
import { segment, type ObservedTotals, type Segmentable, type SegmentFilters } from '../creatives/partition'
import { round4 } from '../hypotheses/proposal'

/**
 * The materiality band: about two standard errors on the conversion count, floored at 10%.
 * Statistical detectability and economic materiality are separate tests, and the floor is
 * the second one: below it a real difference is still too small to act on.
 */
export function materialityBand(conversions: number): number {
  return conversions > 0 ? Math.max(10, 200 / Math.sqrt(conversions)) : 100
}

/** Why a delta is not shown as a number. */
export type Withheld = 'unverified' | 'inProgress' | 'gaps' | 'partial' | 'thin' | 'noBase'

export type Delta =
  | { percent: number; tone: 'good' | 'bad' | 'neutral' }
  | { withheld: Withheld }

/**
 * Whether the segment the tiles show can be compared at all. `null` means it can.
 * `empty` is a known empty: every in-scope creative is covered and none falls in the
 * segment, so there is nothing to compare and nothing to warn about.
 */
export type SegmentCoverage = 'inProgress' | 'unverified' | 'gaps' | 'empty' | null

/** The five tiles that carry a delta. Rankable counts creatives, not the account's figures. */
export type TileMeasure = 'spend' | 'conversions' | 'roas' | 'aov' | 'impressions'

/**
 * Coverage over exactly the segment the totals sum, in the contract's order. The gaps
 * lists come from the server and name every in-scope creative that is not covered in a
 * window, observed or not, with the attributes the filters read; they are filtered here
 * by the same `segment` the totals use, so the check and the figures describe the same
 * creatives. No contribution is dropped to make a segment read complete.
 */
export function segmentCoverage(
  current: WindowCoverage,
  previous: WindowCoverage,
  rows: Segmentable[],
  prior: Segmentable[],
  filters: SegmentFilters,
): SegmentCoverage {
  if (current.state === 'inProgress') return 'inProgress'
  if (current.state === 'unverified' || previous.state === 'unverified') return 'unverified'
  if (segment(current.gaps, filters).length > 0 || segment(previous.gaps, filters).length > 0) return 'gaps'
  if (segment(rows, filters).length === 0 && segment(prior, filters).length === 0) return 'empty'
  return null
}

/** The partial flags a tile's value is built from. */
const READS: Record<TileMeasure, (keyof ObservedTotals['partial'])[]> = {
  spend: ['spend'],
  conversions: ['conversions'],
  impressions: ['impressions'],
  roas: ['spend', 'conversionValue'],
  aov: ['conversionValue', 'conversions'],
}

/** The tiles that estimate a rate and so carry the sample threshold. Spend and
 * impressions are totals the platform counted. */
const SAMPLED: ReadonlySet<TileMeasure> = new Set(['conversions', 'roas', 'aov'])

/**
 * One tile's change against the previous period, or why it is withheld. `null` means the
 * tile carries no delta slot at all: an empty segment has nothing to compare.
 *
 * Withheld, in this order: the segment is not covered; either period is partial on a
 * measure the tile reads; for the sampled tiles, either period is below the provisional
 * line; the previous value is zero or unknown, or the current one is unknown.
 *
 * Only ROAS carries a tone, and only past the outcome materiality band the verdicts use,
 * measured on the smaller of the two samples. The band must be exceeded, and it is compared
 * with the percent at the contract's four-decimal precision, not with the whole percent the
 * card displays: a move of exactly 10% computes as 10.000000000000009 at full precision,
 * and would otherwise clear a 10% band it only meets.
 */
export function delta(
  measure: TileMeasure,
  current: ObservedTotals,
  previous: ObservedTotals,
  ctx: { coverage: SegmentCoverage; thresholds: ConfidenceThresholds },
): Delta | null {
  if (ctx.coverage === 'empty') return null
  if (ctx.coverage !== null) return { withheld: ctx.coverage }
  if (READS[measure].some((key) => current.partial[key] || previous.partial[key])) return { withheld: 'partial' }
  if (
    SAMPLED.has(measure)
    && ((current.conversions ?? 0) < ctx.thresholds.provisionalAt
      || (previous.conversions ?? 0) < ctx.thresholds.provisionalAt)
  ) {
    return { withheld: 'thin' }
  }
  const now = current[measure]
  const before = previous[measure]
  if (now === null || before === null || before === 0) return { withheld: 'noBase' }

  const percent = ((now - before) / before) * 100
  if (measure !== 'roas') return { percent, tone: 'neutral' }
  const band = materialityBand(Math.min(current.conversions ?? 0, previous.conversions ?? 0))
  const compared = round4(percent)
  const tone = compared > band ? 'good' : compared < -band ? 'bad' : 'neutral'
  return { percent, tone }
}
